import { useState, useEffect } from "react";
import brushTeethImage from "@/assets/brush-teeth.jpg";
import listeningImage from "@/assets/listening.jpg";
import screenTimeImage from "@/assets/screen-time.jpg";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { cn } from "@/lib/utils";

const benefits = [
  {
    image: brushTeethImage,
    title: "Builds Good Habits",
    description: "Stories that teach brushing, eating healthy, sharing, and kindness through fun adventures.",
    gradient: "from-[hsl(var(--playful-orange))] to-[hsl(var(--playful-pink))]",
  },
  {
    image: listeningImage,
    title: "Boosts Listening Skills",
    description: "Audio stories help develop imagination, vocabulary, and attention span in young minds.",
    gradient: "from-[hsl(var(--playful-pink))] to-[hsl(var(--playful-purple))]",
  },
  {
    image: screenTimeImage,
    title: "Reduces Screen Time",
    description: "Audio-first approach gives eyes a break while keeping kids entertained and learning.",
    gradient: "from-[hsl(var(--playful-purple))] to-[hsl(var(--playful-teal))]",
  },
];

const BenefitsSection = () => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  return (
    <section className="py-16 md:py-24 px-4 bg-gradient-to-b from-[hsl(var(--playful-purple),0.05)] to-background">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1 bg-[hsl(var(--playful-teal),0.2)] text-[hsl(var(--playful-teal))] rounded-full text-sm font-medium mb-4">
             Why Parents Love Us
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
            Stories That{" "}
            <span className="bg-gradient-to-r from-[hsl(var(--playful-pink))] to-[hsl(var(--playful-purple))] bg-clip-text text-transparent">
              Actually Teach
            </span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            More than entertainment — these stories shape character and build life skills.
          </p>
        </div>

        <div className="relative px-16">
          <Carousel 
            opts={{ loop: true }} 
            plugins={[Autoplay({ delay: 3000, stopOnInteraction: false })]}
            setApi={setApi}
            className="w-full"
          >
            <CarouselContent>
              {benefits.map((benefit, index) => (
                <CarouselItem key={index}>
                  <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center p-4">
                    {/* Left - Text */}
                    <div className="order-2 md:order-1 text-center md:text-left">
                      <h3 className="font-display text-xl md:text-2xl font-bold text-foreground mb-4">
                        {benefit.title}
                      </h3>
                      <p className="text-muted-foreground text-base leading-relaxed">
                        {benefit.description}
                      </p>
                    </div>

                    {/* Right - Blob-shaped Image */}
                    <div className="order-1 md:order-2 relative flex justify-center">
                      {/* Background blob shadow */}
                      <div 
                        className={`absolute inset-0 bg-gradient-to-br ${benefit.gradient} opacity-20 blur-2xl scale-90`}
                        style={{
                          borderRadius: "63% 37% 54% 46% / 55% 48% 52% 45%",
                        }}
                      />
                      
                      {/* Image with blob shape */}
                      <div 
                        className="relative w-72 h-72 md:w-96 md:h-96 overflow-hidden shadow-xl"
                        style={{
                          borderRadius: "63% 37% 54% 46% / 55% 48% 52% 45%",
                        }}
                      >
                        <img
                          src={benefit.image}
                          alt={benefit.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            
            <CarouselPrevious className="-left-4 bg-[hsl(var(--playful-teal))] text-white hover:bg-[hsl(var(--playful-teal),0.8)] border-none" />
            <CarouselNext className="-right-4 bg-[hsl(var(--playful-teal))] text-white hover:bg-[hsl(var(--playful-teal),0.8)] border-none" />
          </Carousel>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {benefits.map((_, index) => (
              <button
                key={index}
                className={cn(
                  "w-2.5 h-2.5 rounded-full transition-colors",
                  current === index 
                    ? "bg-[hsl(var(--playful-teal))]" 
                    : "bg-muted-foreground/30"
                )}
                onClick={() => api?.scrollTo(index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
